import { AlertTriangleIcon, FocusIcon, InfoIcon, SirenIcon } from 'lucide-react';
import { Problem, ProblemSeverity } from './types';
import { clsx } from 'clsx';
import { formatTime } from '../../../utils/format';
import { Button } from '@@/ui/primitives';

const getSeverityIcon = (severity: ProblemSeverity) => {
  switch (severity) {
    case ProblemSeverity.CRITICAL:
      return <SirenIcon className='size-4 text-red-950' />;
    case ProblemSeverity.HIGH:
      return <SirenIcon className='size-4 text-red-950' />;
    case ProblemSeverity.MEDIUM:
      return <AlertTriangleIcon className='size-4 text-orange-950' />;
    case ProblemSeverity.LOW:
      return <InfoIcon className='size-4 text-blue-950' />;
    default:
      return <AlertTriangleIcon className='size-4 text-neutral-950' />;
  }
};

const getSeverityColor = (severity: ProblemSeverity) => {
  switch (severity) {
    case ProblemSeverity.CRITICAL:
      return 'bg-red-50';
    case ProblemSeverity.HIGH:
      return 'bg-red-50';
    case ProblemSeverity.MEDIUM:
      return 'bg-orange-100';
    case ProblemSeverity.LOW:
      return 'bg-blue-50';
    default:
      return 'bg-neutral-50';
  }
};

const getSeverityTextColor = (severity: ProblemSeverity, isSecondary = false) => {
  if (isSecondary) {
    switch (severity) {
      case ProblemSeverity.CRITICAL:
        return 'text-red-950';
      case ProblemSeverity.HIGH:
        return 'text-red-950';
      case ProblemSeverity.MEDIUM:
        return 'text-orange-950';
      case ProblemSeverity.LOW:
        return 'text-blue-950';
      default:
        return 'text-neutral-950';
    }
  }

  switch (severity) {
    case ProblemSeverity.CRITICAL:
      return 'text-red-950';
    case ProblemSeverity.HIGH:
      return 'text-red-950';
    case ProblemSeverity.MEDIUM:
      return 'text-orange-950';
    case ProblemSeverity.LOW:
      return 'text-blue-950';
    default:
      return 'text-neutral-950';
  }
};

const getSourceBadge = (source: string) => {
  switch (source) {
    case 'IVR_LOG':
      return {
        text: 'Interaction Log',
        className: 'text-red-800 border border-red-200'
      };
    case 'WISDOM_TRANSCRIPT':
      return {
        text: 'Wisdom Transcript',
        className: 'text-purple-800 border border-purple-200'
      };
    case 'FLOW_LOG':
      return {
        text: 'Flow Log',
        className: 'text-fuchsia-800 border border-fuchsia-200'
      };
    default:
      return {
        text: source.replace('_', ' '),
        className: 'text-neutral-800 border border-neutral-200'
      };
  }
};

export const ProblemItem = (props: { problem: Problem; onFocusClick?: (problem: Problem) => void }) => {
  const { problem, onFocusClick } = props;
  const canNavigate = problem.index !== undefined && problem.source !== 'WISDOM_TRANSCRIPT';

  return (
    <div className={clsx('rounded-xl p-3 px-4 mb-3', 'transition', getSeverityColor(problem.severity))}>
      <div className='flex justify-between mb-2'>
        <div className='flex items-center gap-4'>
          {getSeverityIcon(problem.severity)}
          <div className='flex flex-col'>
            <h4 className={clsx('text-sm font-semibold', getSeverityTextColor(problem.severity))}>{problem.title}</h4>
            <p className={clsx('text-xs font-medium', getSeverityTextColor(problem.severity))}>{problem.description}</p>
          </div>
          {canNavigate && onFocusClick && (
            <Button
              variant='ghost'
              size='xs'
              className='p-0 text-xs! gap-2 rounded-lg bg-white/50 border-none'
              onClick={(e) => {
                e.stopPropagation();
                onFocusClick(problem);
              }}
            >
              <FocusIcon className='size-3 text-neutral-500' />
              View
            </Button>
          )}
        </div>
        <div className='flex flex-row justify-between items-center'>
          <div className='flex items-center gap-2'>
            <span className={clsx('text-xs font-medium', getSeverityTextColor(problem.severity, true))}>
              {formatTime(problem.timestamp)}
            </span>
            <span
              className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                getSourceBadge(problem.source).className
              )}
            >
              {getSourceBadge(problem.source).text}
            </span>
          </div>
        </div>
      </div>

      {problem.suggestions && problem.suggestions.length > 0 && (
        <div className='ml-8 pt-2'>
          <h5 className={clsx('text-xs font-semibold mb-1', getSeverityTextColor(problem.severity, true))}>
            Suggestions
          </h5>
          <ul className={clsx('text-xs space-y-1', getSeverityTextColor(problem.severity, true))}>
            {problem.suggestions.map((suggestion, index) => (
              <li key={index} className='flex items-start'>
                <span className='mr-1'>•</span>
                <span className='font-medium'>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
